import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import { formatDate, formatMoney, todayISO } from '../lib/format'
import type { DebtKind } from '../types'
import { DEBT_KIND_LABEL } from '../types'

/** Взаиморасчёты: займы в обе стороны, возвраты и зачёты работой. */
export default function Debts() {
  const qc = useQueryClient()
  const { can, canSeeAll, user } = useAuth()
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<number | null>(null)

  const [draft, setDraft] = useState({
    user_id: '',
    kind: 'issued' as DebtKind,
    amount: '',
    occurred_on: todayISO(),
    comment: '',
  })

  const balances = useQuery({ queryKey: ['debt-balances'], queryFn: () => api.debtBalances() })
  const kinds = useQuery({ queryKey: ['debt-kinds'], queryFn: () => api.debtKinds() })
  const entries = useQuery({
    queryKey: ['debts', selected],
    queryFn: () => api.debts(selected ?? undefined),
  })

  const canManage = can('debts.manage')

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['debt-balances'] })
    qc.invalidateQueries({ queryKey: ['debts'] })
    qc.invalidateQueries({ queryKey: ['payout-preview'] })
  }

  const create = useMutation({
    mutationFn: () =>
      api.createDebt({
        user_id: Number(draft.user_id),
        kind: draft.kind,
        amount: Number(draft.amount),
        occurred_on: draft.occurred_on,
        comment: draft.comment || null,
      }),
    onSuccess: () => {
      setDraft({ ...draft, amount: '', comment: '' })
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось записать операцию'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteDebt(id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить операцию'),
  })

  const rows = balances.data ?? []
  const myBalance = rows.find((b) => b.user_id === user?.id)

  return (
    <div className="section">
      <h2>Взаиморасчёты</h2>

      {error && (
        <div className="error" onClick={() => setError('')}>
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Здесь ведутся займы в обе стороны. <b>Плюс</b> — сотрудник должен компании,{' '}
        <b>минус</b> — компания должна сотруднику. Работой долг гасится при формировании
        ведомости: сумма зачёта уходит сюда отдельной строкой.
      </p>

      {!canSeeAll && myBalance && (
        <div className="kpi" style={{ maxWidth: 280, marginBottom: 14 }}>
          <div className="label">Ваш баланс</div>
          <div className={`value ${Number(myBalance.balance) > 0 ? 'debt-owes' : 'debt-owed'}`}>
            {formatMoney(myBalance.balance)}
          </div>
          <div className="sub">
            {Number(myBalance.balance) > 0
              ? 'вы должны компании'
              : Number(myBalance.balance) < 0
                ? 'компания должна вам'
                : 'расчёты закрыты'}
          </div>
        </div>
      )}

      {canSeeAll && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th className="num">Выдано</th>
                <th className="num">Вернул</th>
                <th className="num">Заняли у него</th>
                <th className="num">Вернули ему</th>
                <th className="num">Зачтено работой</th>
                <th className="num">Баланс</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    Операций пока нет
                  </td>
                </tr>
              )}
              {rows.map((b) => (
                <tr key={b.user_id} className={selected === b.user_id ? 'running' : undefined}>
                  <td>
                    <span className="client-name">{b.user_name}</span>
                  </td>
                  <td className="num mono">{formatMoney(b.issued)}</td>
                  <td className="num mono">{formatMoney(b.returned)}</td>
                  <td className="num mono">{formatMoney(b.borrowed)}</td>
                  <td className="num mono">{formatMoney(b.repaid)}</td>
                  <td className="num mono">{formatMoney(b.work_offset)}</td>
                  <td className="num mono">
                    <b className={Number(b.balance) > 0 ? 'debt-owes' : 'debt-owed'}>
                      {formatMoney(b.balance)}
                    </b>
                  </td>
                  <td>
                    <button
                      className="btn small"
                      onClick={() => setSelected(selected === b.user_id ? null : b.user_id)}
                    >
                      {selected === b.user_id ? 'Скрыть' : `История (${b.entries_count})`}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && (
        <div className="toolbar" style={{ marginTop: 14 }}>
          <div className="field">
            <label>Сотрудник</label>
            <select
              value={draft.user_id}
              onChange={(e) => setDraft({ ...draft, user_id: e.target.value })}
            >
              <option value="">— выберите —</option>
              {rows.map((b) => (
                <option key={b.user_id} value={b.user_id}>
                  {b.user_name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Операция</label>
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as DebtKind })}
            >
              {kinds.data?.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Сумма, ₽</label>
            <input
              type="number"
              min="0"
              step="0.01"
              style={{ width: 120 }}
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Дата</label>
            <input
              type="date"
              value={draft.occurred_on}
              onChange={(e) => setDraft({ ...draft, occurred_on: e.target.value })}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 160 }}>
            <label>Комментарий</label>
            <input
              value={draft.comment}
              onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
              placeholder="на инструмент"
            />
          </div>
          <button
            className="btn primary"
            disabled={!draft.user_id || !draft.amount || create.isPending}
            onClick={() => create.mutate()}
          >
            Записать
          </button>
        </div>
      )}

      {(selected !== null || !canSeeAll) && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Дата</th>
                {canSeeAll && <th>Сотрудник</th>}
                <th>Операция</th>
                <th className="num">Сумма</th>
                <th className="num">В баланс</th>
                <th>Комментарий</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {entries.data?.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty">
                    Операций нет
                  </td>
                </tr>
              )}
              {entries.data?.map((e) => (
                <tr key={e.id}>
                  <td className="mono nowrap">{formatDate(e.occurred_on)}</td>
                  {canSeeAll && <td>{e.user_name}</td>}
                  <td>
                    {DEBT_KIND_LABEL[e.kind]}
                    {e.payout_id && <span className="cell-sub">из ведомости</span>}
                  </td>
                  <td className="num mono nowrap">{formatMoney(e.amount)}</td>
                  <td className="num mono nowrap">
                    <span className={Number(e.signed_amount) > 0 ? 'debt-owes' : 'debt-owed'}>
                      {Number(e.signed_amount) > 0 ? '+' : ''}
                      {formatMoney(e.signed_amount)}
                    </span>
                  </td>
                  <td className="wrap">{e.comment ?? ''}</td>
                  {canManage && (
                    <td>
                      {e.payout_id ? (
                        <span
                          style={{ color: 'var(--muted)', fontSize: 12 }}
                          title="Зачёт живёт вместе с ведомостью"
                        >
                          —
                        </span>
                      ) : (
                        <button
                          className="btn small danger"
                          onClick={() => {
                            if (confirm('Удалить операцию?')) remove.mutate(e.id)
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
